import { useState } from "react";
import SideMenu from "../components/SideMenu";
import { setupNewSiteConnection } from "services/site.service";

type Integrations = { GA: boolean; WC: boolean; MC: boolean };
type ApiKeys = {
  GA_PROPERTY_ID: string;
  GOOGLE_APPLICATION_CREDENTIALS: string;
  WC_URL: string;
  WC_CONSUMER_KEY: string;
  WC_CONSUMER_SECRET: string;
  MC_API_KEY: string;
  MC_SERVER_PREFIX: string;
};

type FormDataState = {
  clientName: string;
  adminEmail: string;
  refCode: string;
  integrations: Integrations;
  apiKeys: ApiKeys;
};

export default function ClientForm() {
  const [formData, setFormData] = useState<FormDataState>({
    clientName: "",
    adminEmail: "",
    refCode: "",
    integrations: { GA: false, WC: false, MC: false },
    apiKeys: {
      GA_PROPERTY_ID: "",
      GOOGLE_APPLICATION_CREDENTIALS: "",
      WC_URL: "",
      WC_CONSUMER_KEY: "",
      WC_CONSUMER_SECRET: "",
      MC_API_KEY: "",
      MC_SERVER_PREFIX: "",
    },
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    if (type === "checkbox") {
      setFormData((prev) => ({
        ...prev,
        integrations: { ...prev.integrations, [name]: checked } as Integrations,
      }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value } as FormDataState));
    }
  };

  const handleApiKeyChange = (key: keyof ApiKeys, value: string) => {
    setFormData((prev) => ({
      ...prev,
      apiKeys: { ...prev.apiKeys, [key]: value },
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    setupNewSiteConnection(e, formData.apiKeys);
  };

  return (
    <div className="grid min-h-screen grid-cols-1 md:grid-cols-[240px_1fr]">
      <SideMenu />
      <div className="container max-w-2xl py-10">
        <h1 className="text-2xl font-semibold tracking-tight mb-6">
          Add website
        </h1>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="mb-1 block text-sm font-medium">
              Client Name
            </label>
            <input
              type="text"
              name="clientName"
              value={formData.clientName}
              onChange={handleChange}
              className="w-full rounded-md border bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              required
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium">
              Admin Email
            </label>
            <input
              type="email"
              name="adminEmail"
              value={formData.adminEmail}
              onChange={handleChange}
              className="w-full rounded-md border bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              required
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium">Ref Code</label>
            <input
              type="text"
              name="refCode"
              value={formData.refCode}
              onChange={handleChange}
              className="w-full rounded-md border bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium">
              Enable Integrations
            </label>
            <div className="space-y-2">
              {["GA", "WC", "MC"].map((service) => (
                <div key={service} className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    name={service}
                    checked={(formData.integrations as any)[service] as boolean}
                    onChange={handleChange}
                    className="h-4 w-4 rounded border bg-background"
                  />
                  <span>
                    {service === "GA"
                      ? "Google Analytics"
                      : service === "WC"
                      ? "WooCommerce"
                      : "Mailchimp"}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {formData.integrations.GA && (
            <div className="space-y-2">
              <h3 className="text-sm font-medium">Google Analytics Keys</h3>
              <input
                type="text"
                placeholder="GA_PROPERTY_ID"
                value={formData.apiKeys.GA_PROPERTY_ID}
                onChange={(e) =>
                  handleApiKeyChange("GA_PROPERTY_ID", e.target.value)
                }
                className="w-full rounded-md border bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                required
              />
              <input
                type="text"
                placeholder="GOOGLE_APPLICATION_CREDENTIALS"
                value={formData.apiKeys.GOOGLE_APPLICATION_CREDENTIALS}
                onChange={(e) =>
                  handleApiKeyChange(
                    "GOOGLE_APPLICATION_CREDENTIALS",
                    e.target.value
                  )
                }
                className="w-full rounded-md border bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                required
              />
            </div>
          )}

          {formData.integrations.WC && (
            <div className="space-y-2">
              <h3 className="text-sm font-medium">WooCommerce Keys</h3>
              <input
                type="text"
                placeholder="WC_URL"
                value={formData.apiKeys.WC_URL}
                onChange={(e) => handleApiKeyChange("WC_URL", e.target.value)}
                className="w-full rounded-md border bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                required
              />
              <input
                type="text"
                placeholder="WC_CONSUMER_KEY"
                value={formData.apiKeys.WC_CONSUMER_KEY}
                onChange={(e) =>
                  handleApiKeyChange("WC_CONSUMER_KEY", e.target.value)
                }
                className="w-full rounded-md border bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                required
              />
              <input
                type="text"
                placeholder="WC_CONSUMER_SECRET"
                value={formData.apiKeys.WC_CONSUMER_SECRET}
                onChange={(e) =>
                  handleApiKeyChange("WC_CONSUMER_SECRET", e.target.value)
                }
                className="w-full rounded-md border bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                required
              />
            </div>
          )}

          {formData.integrations.MC && (
            <div className="space-y-2">
              <h3 className="text-sm font-medium">Mailchimp Keys</h3>
              <input
                type="text"
                placeholder="MC_API_KEY"
                value={formData.apiKeys.MC_API_KEY}
                onChange={(e) =>
                  handleApiKeyChange("MC_API_KEY", e.target.value)
                }
                className="w-full rounded-md border bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                required
              />
              <input
                type="text"
                placeholder="MC_SERVER_PREFIX"
                value={formData.apiKeys.MC_SERVER_PREFIX}
                onChange={(e) =>
                  handleApiKeyChange("MC_SERVER_PREFIX", e.target.value)
                }
                className="w-full rounded-md border bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                required
              />
            </div>
          )}

          <button
            type="submit"
            className="w-full rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow hover:opacity-90"
          >
            Submit
          </button>
        </form>
      </div>
    </div>
  );
}

